import { somar, subtrair, multiplicar, type Vetor3 } from '../geometria/vetor3'
import type { ProporcoesVitima } from './perfisVitima'
import {
  POSE_SENTADA,
  direcoesDaVitima,
  eixoColunaDaVitima,
  posicaoCabeca,
  type ColocacaoVitima,
  type PoseVitima,
} from './rigVitima'
import type { AchadoXabcde } from './cenariosExtracao'

export type RegiaoAnatomica = NonNullable<AchadoXabcde['regiao']>

/** fração do eixo da coluna (base → topo) onde fica o centro aproximado de cada região */
const FRACAO_TORAX = 0.62
const FRACAO_ABDOME = 0.22
/** fração de (coxa + perna) usada pra descer da pelve até o marcador do membro inferior */
const FRACAO_MEMBRO_INFERIOR = 0.5

const interpolar = (de: Vetor3, para: Vetor3, fracao: number): Vetor3 =>
  somar(de, multiplicar(subtrair(para, de), fracao))

/**
 * Centro aproximado de uma região anatômica, em coordenadas de mundo, a
 * partir da pose e colocação ATUAIS — acompanha postura e movimento porque
 * deriva do mesmo eixo da coluna que `calcularAnguloExtracao` usa, não de um
 * offset fixo desenhado à parte.
 *
 * `membroInferior` é a exceção deliberada: o rig não modela o ângulo do
 * quadril/joelho fora de `VitimaProcedural`, então o marcador continua o
 * eixo da coluna para baixo, através da pelve — uma região aproximada, não
 * um ponto anatômico preciso. É coerente com o que o achado pede (destacar
 * "membro inferior" como região, não localizar uma lesão).
 */
export function posicaoRegiaoAnatomica(
  regiao: RegiaoAnatomica,
  colocacao: ColocacaoVitima,
  proporcoes: ProporcoesVitima,
  pose: PoseVitima = POSE_SENTADA,
): Vetor3 {
  switch (regiao) {
    case 'cabeca':
      return posicaoCabeca(colocacao, proporcoes, pose)
    case 'torax': {
      const { baseColuna, topoColuna } = eixoColunaDaVitima(colocacao, proporcoes, pose)
      return interpolar(baseColuna, topoColuna, FRACAO_TORAX)
    }
    case 'abdome': {
      const { baseColuna, topoColuna } = eixoColunaDaVitima(colocacao, proporcoes, pose)
      return interpolar(baseColuna, topoColuna, FRACAO_ABDOME)
    }
    case 'membroInferior': {
      const { eixo } = direcoesDaVitima(colocacao, pose)
      const comprimento = (proporcoes.comprimentoCoxa + proporcoes.comprimentoPerna) * FRACAO_MEMBRO_INFERIOR
      return somar(colocacao.posicaoPelve, multiplicar(eixo, -comprimento))
    }
    case 'corpoTodo':
      return colocacao.posicaoPelve
  }
}
